import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "@/lib/apiClient";
import {
  computeBillableElectricityKwh,
  electricityModeNeedsKwh,
  type ElectricityBillingMode,
} from "@/lib/electricityUsage";
import {
  billingAddressToPayload,
  guestToBillingAddressForm,
  validateBillingAddressForm,
  type BillingAddressFormState,
} from "@/lib/invoiceBillingAddress";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatStayAmount } from "@/lib/money";
import { canWriteInvoice } from "@/lib/permissions";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { InvoiceBillingAddressFields } from "@/components/invoices/InvoiceBillingAddressFields";
import type { Booking, Discount, Guest, Invoice, InvoiceListResponse } from "@/types/camping";
import type { InvoiceType } from "@/lib/invoiceType";

interface DepositSettings {
  mode: "percent" | "fixed" | "first_night";
  percent: string;
  fixed_amount: string | null;
  cancellation_deadline_days?: number;
  cancellation_deadline_time?: string;
}

interface TenantBillingSettings {
  electricity_billing: {
    mode: ElectricityBillingMode;
    included_kwh_per_night: string | null;
  };
  deposit?: DepositSettings;
}

const DEFAULT_DEPOSIT: DepositSettings = {
  mode: "percent",
  percent: "30",
  fixed_amount: null,
};

function partyLabel(booking: Booking): string | null {
  const adults = booking.adults ?? 0;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  const parts: string[] = [];
  if (adults === 1) parts.push("1 Erwachsener");
  else if (adults > 0) parts.push(`${adults} Erwachsene`);
  if (children === 1) parts.push("1 Kind");
  else if (children > 0) parts.push(`${children} Kinder`);
  if (dogs === 1) parts.push("1 Hund");
  else if (dogs > 0) parts.push(`${dogs} Hunde`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function formatPercent(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(num);
}

function estimateDeposit(total: number, deposit: DepositSettings, nights: number): number {
  if (!(total > 0)) return 0;
  if (deposit.mode === "fixed") {
    const fixed = Number(deposit.fixed_amount ?? 0);
    if (!Number.isFinite(fixed) || fixed <= 0) return 0;
    return Math.min(fixed, total);
  }
  if (deposit.mode === "first_night") {
    if (!(nights > 0)) return 0;
    return Math.min(Math.round((total / nights) * 100) / 100, total);
  }
  const percent = Number(deposit.percent);
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  const cents = Math.round(total * 100);
  return Math.min(Math.round((cents * percent) / 100) / 100, total);
}

export interface CreateBookingInvoiceModalProps {
  open: boolean;
  booking: Booking | null;
  userGroups: string[];
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
}

export function CreateBookingInvoiceModal({
  open,
  booking,
  userGroups,
  onClose,
  onCreated,
}: CreateBookingInvoiceModalProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [depositSettings, setDepositSettings] = useState<DepositSettings>(DEFAULT_DEPOSIT);
  const [billingMode, setBillingMode] = useState<ElectricityBillingMode>("flat_per_night");
  const [includedKwhPerNight, setIncludedKwhPerNight] = useState<number | null>(null);
  const [electricityKwhInput, setElectricityKwhInput] = useState("");
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [billingAddress, setBillingAddress] = useState<BillingAddressFormState>({
    override: false,
    company_name: "",
    name: "",
    street: "",
    postal_code: "",
    city: "",
    country_code: "DE",
  });

  useEffect(() => {
    if (!open || !booking) {
      setInvoices([]);
      setError(null);
      setAction(null);
      return;
    }
    setElectricityKwhInput(
      booking.electricity_kwh != null ? String(booking.electricity_kwh) : "",
    );
    setLoading(true);
    Promise.all([
      api.get<InvoiceListResponse>(`/api/v1/invoices?booking_id=${booking.id}`),
      api.get<Guest>(`/api/v1/guests/${booking.guest_id}`),
    ])
      .then(([invoiceRes, guest]) => {
        setInvoices(invoiceRes.items);
        setBillingAddress(guestToBillingAddressForm(guest));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, booking]);

  useEffect(() => {
    if (!open) return;
    api
      .get<TenantBillingSettings>("/api/v1/settings")
      .then((s) => {
        setDepositSettings(normalizeDeposit(s.deposit));
        setBillingMode(s.electricity_billing.mode);
        setIncludedKwhPerNight(
          s.electricity_billing.included_kwh_per_night != null
            ? Number(s.electricity_billing.included_kwh_per_night)
            : null,
        );
      })
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!open || !canInvoice) return;
    api
      .get<Discount[]>("/api/v1/discounts")
      .then(setDiscounts)
      .catch(() => setDiscounts([]));
  }, [open, canInvoice]);

  const activeInvoices = useMemo(
    () => invoices.filter((inv) => inv.status !== "cancelled"),
    [invoices],
  );

  const hasDeposit = activeInvoices.some((inv) => inv.invoice_type === "deposit");
  const hasFinal = activeInvoices.some((inv) => inv.invoice_type === "final");
  const hasFull = activeInvoices.some((inv) => inv.invoice_type === "full");
  const issuedDeposit = activeInvoices.some(
    (inv) => inv.invoice_type === "deposit" && isInvoiceFinalized(inv.status),
  );
  const hasDraftOfType = (type: InvoiceType) =>
    invoices.some((inv) => inv.status === "draft" && inv.invoice_type === type);

  const canCreateDeposit =
    canInvoice && !hasDeposit && !hasFinal && !hasFull && !hasDraftOfType("deposit");
  const canCreateFull =
    canInvoice && !hasDeposit && !hasFinal && !hasFull && !hasDraftOfType("full");
  const canCreateFinal =
    canInvoice && issuedDeposit && !hasFinal && !hasFull && !hasDraftOfType("final");
  const canCreateAny = canCreateDeposit || canCreateFull || canCreateFinal;

  const needsKwhForInvoice =
    Boolean(booking?.wants_electricity) &&
    (billingMode === "per_kwh" || billingMode === "flat_with_included_kwh");

  async function createDraft(invoiceType: InvoiceType) {
    if (!booking || !canInvoice) return;
    const billingError = validateBillingAddressForm(billingAddress);
    if (billingError) {
      setError(billingError);
      return;
    }
    setAction(`create-${invoiceType}`);
    setError(null);
    try {
      const payload: {
        booking_id: string;
        invoice_type: InvoiceType;
        electricity_kwh?: string;
        discount_id?: string;
        billing_address: ReturnType<typeof billingAddressToPayload>;
      } = {
        booking_id: booking.id,
        invoice_type: invoiceType,
        billing_address: billingAddressToPayload(billingAddress),
      };
      if (needsKwhForInvoice) {
        const kwh = electricityKwhInput.trim();
        if (!kwh) {
          setError("Bitte den Stromverbrauch in kWh eingeben.");
          return;
        }
        payload.electricity_kwh = kwh;
      }
      if (selectedDiscountId && (invoiceType === "full" || invoiceType === "final")) {
        payload.discount_id = selectedDiscountId;
      }
      const draft = await api.post<Invoice>("/api/v1/invoices/from-booking", payload);
      onCreated(draft.id);
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Rechnungsentwurf konnte nicht erstellt werden.",
      );
    } finally {
      setAction(null);
    }
  }

  const parsedKwh = electricityKwhInput.trim() ? Number(electricityKwhInput) : null;
  const billableKwh =
    booking?.wants_electricity && electricityModeNeedsKwh(billingMode)
      ? computeBillableElectricityKwh(
          billingMode,
          booking.nights,
          parsedKwh != null && !Number.isNaN(parsedKwh) ? parsedKwh : null,
          includedKwhPerNight,
        )
      : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rechnung erstellen"
      description={
        booking
          ? `Buchung ${booking.booking_number} · ${booking.guest_name ?? "Gast"}`
          : undefined
      }
      size="lg"
      footer={
        <Button type="button" variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
      }
    >
      {!booking ? null : loading ? (
        <p className="text-sm text-label-secondary">Lade Rechnungsdaten…</p>
      ) : !canInvoice ? (
        <p className="text-sm text-label-secondary">
          Sie haben keine Berechtigung, Rechnungen anzulegen.
        </p>
      ) : !canCreateAny ? (
        <p className="text-sm text-label-secondary">
          Für diese Buchung kann derzeit keine weitere Rechnung angelegt werden.
        </p>
      ) : (
        <div className="space-y-4">
          {needsKwhForInvoice ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-label">Stromverbrauch (kWh)</span>
              <input
                type="number"
                min="0"
                step="0.1"
                className="eh-input w-full"
                value={electricityKwhInput}
                onChange={(e) => setElectricityKwhInput(e.target.value)}
              />
              {billableKwh != null ? (
                <span className="mt-1 block text-xs text-label-tertiary">
                  Abrechenbar: {billableKwh} kWh
                </span>
              ) : null}
            </label>
          ) : null}

          {discounts.length > 0 ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-label">Rabatt (optional)</span>
              <select
                className="eh-input w-full"
                value={selectedDiscountId}
                onChange={(e) => setSelectedDiscountId(e.target.value)}
              >
                <option value="">Kein Rabatt</option>
                {discounts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <InvoiceChoiceSummary
            booking={booking}
            depositSettings={depositSettings}
            issuedDeposit={activeInvoices.find(
              (inv) => inv.invoice_type === "deposit" && isInvoiceFinalized(inv.status),
            )}
            needsKwh={needsKwhForInvoice}
          />

          <InvoiceBillingAddressFields
            value={billingAddress}
            onChange={setBillingAddress}
            disabled={!!action}
          />

          <div
            className={
              [canCreateFull, canCreateFinal, canCreateDeposit].filter(Boolean).length > 1
                ? "grid gap-3 sm:grid-cols-2"
                : "grid gap-3"
            }
          >
            {canCreateFull ? (
              <InvoiceChoiceCard
                title="Vollrechnung"
                detail="Alle Positionen des Aufenthalts in einer Rechnung. Danach ist keine Anzahlung und keine Schlussrechnung mehr möglich."
                amount={formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
                amountHint={booking.price_pending ? null : "Gesamtbetrag des Aufenthalts"}
              >
                <Button
                  type="button"
                  className="w-full"
                  loading={action === "create-full"}
                  disabled={!!action}
                  onClick={() => void createDraft("full")}
                >
                  Vollrechnung erstellen
                </Button>
              </InvoiceChoiceCard>
            ) : null}
            {canCreateFinal ? (
              <InvoiceChoiceCard
                title="Schlussrechnung"
                detail="Der Rest nach Verrechnung der bereits ausgestellten Anzahlung. Enthalten sind alle Aufenthaltspositionen abzüglich der Anzahlung."
                amount={formatStayAmount(
                  finalRemainder(booking, activeInvoices),
                  booking.currency,
                  booking.price_pending,
                )}
                amountHint="Voraussichtlicher Restbetrag"
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  loading={action === "create-final"}
                  disabled={!!action}
                  onClick={() => void createDraft("final")}
                >
                  Schlussrechnung erstellen
                </Button>
              </InvoiceChoiceCard>
            ) : null}
            {canCreateDeposit ? (
              <InvoiceChoiceCard
                title="Anzahlung"
                detail={depositDetail(depositSettings)}
                amount={
                  booking.price_pending
                    ? formatStayAmount(null, booking.currency, true)
                    : formatStayAmount(
                        estimateDeposit(
                          Number(booking.total_amount),
                          depositSettings,
                          booking.nights,
                        ),
                        booking.currency,
                      )
                }
                amountHint={
                  booking.price_pending
                    ? null
                    : depositSettings.mode === "first_night"
                      ? "Vorschau als Durchschnitt je Nacht; die Rechnung nimmt die Anreisenacht"
                      : `von ${formatStayAmount(booking.total_amount, booking.currency)} Aufenthalt`
                }
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  loading={action === "create-deposit"}
                  disabled={!!action}
                  onClick={() => void createDraft("deposit")}
                >
                  Anzahlung erstellen
                </Button>
              </InvoiceChoiceCard>
            ) : null}
          </div>

          {error ? <p className="text-sm text-status-red">{error}</p> : null}
        </div>
      )}
    </Modal>
  );
}

function normalizeDeposit(raw: DepositSettings | undefined): DepositSettings {
  if (!raw) return DEFAULT_DEPOSIT;
  const mode =
    raw.mode === "fixed" || raw.mode === "first_night" || raw.mode === "percent"
      ? raw.mode
      : "percent";
  return {
    mode,
    percent: String(raw.percent ?? DEFAULT_DEPOSIT.percent),
    fixed_amount: raw.fixed_amount == null ? null : String(raw.fixed_amount),
    cancellation_deadline_days: raw.cancellation_deadline_days,
    cancellation_deadline_time: raw.cancellation_deadline_time,
  };
}

function depositDetail(deposit: DepositSettings): string {
  if (deposit.mode === "fixed") {
    return "Fester Anzahlungsbetrag, höchstens so hoch wie der Aufenthalt. Der Rest wird später mit der Schlussrechnung abgerechnet.";
  }
  if (deposit.mode === "first_night") {
    const days = deposit.cancellation_deadline_days ?? 14;
    const time = deposit.cancellation_deadline_time || "12:00";
    return `Preis der ersten Nacht. Bei Absage bis ${days} Tage vor Anreise, ${time} Uhr, wird der Betrag gutgeschrieben.`;
  }
  return `${formatPercent(deposit.percent)} % des Aufenthaltsbetrags. Der Rest wird später mit der Schlussrechnung abgerechnet.`;
}

function finalRemainder(booking: Booking, invoices: Invoice[]): number | null {
  const deposit = invoices.find(
    (inv) => inv.invoice_type === "deposit" && isInvoiceFinalized(inv.status),
  );
  const total = Number(booking.total_amount);
  const credited = deposit ? Number(deposit.total_amount) : 0;
  if (!Number.isFinite(total)) return null;
  const rest = total - (Number.isFinite(credited) ? credited : 0);
  return Math.max(0, Math.round(rest * 100) / 100);
}

function InvoiceChoiceSummary({
  booking,
  depositSettings,
  issuedDeposit,
  needsKwh,
}: {
  booking: Booking;
  depositSettings: DepositSettings;
  issuedDeposit: Invoice | undefined;
  needsKwh: boolean;
}) {
  const party = partyLabel(booking);
  const depositRule =
    depositSettings.mode === "fixed"
      ? "feste Pauschale"
      : depositSettings.mode === "first_night"
        ? "erste Nacht"
        : `${formatPercent(depositSettings.percent)} %`;

  return (
    <div className="space-y-3 rounded-xl border border-separator bg-fill-quaternary p-4">
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium text-label-tertiary">Aufenthalt</dt>
          <dd className="mt-0.5 text-sm font-medium text-label">
            {formatIsoDateShort(booking.arrival_date)} – {formatIsoDateShort(booking.departure_date)}
            <span className="font-normal text-label-secondary">
              {" "}
              · {booking.nights} {booking.nights === 1 ? "Nacht" : "Nächte"}
            </span>
          </dd>
        </div>
        {party ? (
          <div>
            <dt className="text-xs font-medium text-label-tertiary">Personen</dt>
            <dd className="mt-0.5 text-sm font-medium text-label">{party}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-medium text-label-tertiary">Aufenthaltsbetrag</dt>
          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-label">
            {formatStayAmount(booking.total_amount, booking.currency, booking.price_pending)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-label-tertiary">Anzahlung</dt>
          <dd className="mt-0.5 text-sm text-label">
            {issuedDeposit
              ? `Bereits ausgestellt: ${formatStayAmount(issuedDeposit.total_amount, issuedDeposit.currency)}`
              : depositRule}
          </dd>
        </div>
      </dl>
      {needsKwh ? (
        <p className="text-xs leading-relaxed text-label-secondary">
          Strom wird zusätzlich nach dem eingetragenen Verbrauch berechnet und steht auf der
          Voll- oder Schlussrechnung.
        </p>
      ) : null}
    </div>
  );
}

function InvoiceChoiceCard({
  title,
  detail,
  amount,
  amountHint,
  children,
}: {
  title: string;
  detail: string;
  amount: string;
  amountHint: string | null;
  children: ReactNode;
}) {
  return (
    <article className="flex flex-col gap-3 rounded-xl border border-separator bg-card p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-label">{title}</h3>
        <p className="text-sm leading-relaxed text-label-secondary">{detail}</p>
      </div>
      <div className="mt-auto">
        <p className="text-lg font-semibold tabular-nums text-label">{amount}</p>
        {amountHint ? <p className="text-xs text-label-tertiary">{amountHint}</p> : null}
      </div>
      {children}
    </article>
  );
}
